import { ApprovalEvidence } from './ApprovalEvidence';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Badge, Chip, DataTable, InfoTip, type DataTableColumn } from '@zarya/ui';
import { Icon } from '@zarya/theme';
import { dataCurta, dataHour } from '@app/format';
import { t } from '@app/i18n';
import { useClusterReading } from '@app/reading';
import { useClusterPhotoSince } from '../Runtime/Runtime.api';
import { useApprovals } from './Approvals.api';
import { ChipIdentity, FilterCombo, NoteCount } from './ApprovalsFilters';
import {
  pendingAssignment,
  inExecution,
  inExecutionTip,
  reason as reasonBadge,
  originLabel,
  otherPendencyTip,
  reductionLabel,
  reductionTip,
  severity as severityBadge,
  groupByIdentity,
  comparadaWith,
  identityName,
  otherReleases,
  type Approval,
} from './ApprovalsModel';
import s from './Approvals.module.css';

/** Opções do filtro de motivo. Função: o rótulo vem do catálogo. */
const reasonOptions = () => [
  { value: 'nova_identidade', label: t('approvals.reason.nova_identidade') },
  { value: 'capabilities_alteradas', label: t('approvals.reason.capabilities_alteradas') },
];

/**
 * Uma linha da grade: uma pendência, ou o botão que mostra/esconde as demais
 * releases da mesma identidade.
 *
 * A `DataTable` do produto é plana — sem grupo e sem `colspan` —, e o alvo aqui
 * não é uma grade nova: é uma coluna Identidade que se lê sozinha. Por isso o
 * botão do grupo vive **dentro** da primeira célula, com as outras vazias, e as
 * linhas que ele abre nascem indentadas.
 */
type Row =
  | { kind: 'pendencia'; id: string; approval: Approval; recolhida: boolean }
  | { kind: 'group'; id: string; identityId: string; quantas: number; aberto: boolean };

export interface PendingTabProps {
  /** Total de pendentes segundo o `summary` — a nota "{n} de {m}" o usa. */
  total: number;
  onAbrir: (id: string) => void;
  /** `?identidade=` da URL: a fila de **uma** identidade (rodapé de T5). */
  identityId: string | null;
  /** Tira o `?identidade=` e devolve a fila inteira — o × do chip e "Limpar filtros". */
  onLimparIdentidade: () => void;
}

/**
 * Aba **Pendentes** (FR-057): só o que exige decisão humana, da mais antiga para
 * a mais nova, com a severidade agregada por linha e o ⓘ de outra pendência
 * (FR-058).
 *
 * # AGRUPADA POR IDENTIDADE DESDE 29/08
 *
 * No ACR do lab uma imagem tem dezenas de tags, e o discovery criava uma
 * pendência por digest. A tela virava N linhas do mesmo agente, idênticas, e a
 * única forma de achar a que importa era copiar o digest de cada uma e comparar
 * com o Runtime Status — fora desta tela, à mão.
 *
 * Agora cada identidade abre com **uma** linha: a que está rodando no cluster
 * (selo «Em execução no cluster») ou, quando nenhuma está, a de build mais
 * recente. As demais ficam atrás de «Outras releases desta identidade (N)», que
 * as revela sem sair da página. A regra de qual abre está em
 * `agruparPorIdentidade`, no modelo, e não aqui: ela é decisão de produto e tem
 * teste próprio.
 *
 * A coluna Release passou a mostrar **tag e data do build** em vez do digest: com
 * várias tags por imagem o digest curto não distingue nada para quem decide. O
 * digest inteiro continua em T10, onde é o identificador e é copiável.
 */
export function PendingTab({
  total,
  onAbrir,
  identityId,
  onLimparIdentidade,
}: PendingTabProps) {
  const [search, setBusca] = useState('');
  const [motivo, setMotivo] = useState<string | null>(null);
  const [abertos, setAbertos] = useState<Record<string, boolean>>({});

  const queue = useApprovals({
    status: ['pendente'],
    sort: 'created_at',
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(motivo ? { reason: motivo as 'nova_identidade' | 'capabilities_alteradas' } : {}),
    // O recorte por identidade é do servidor (`identity_id` de `listApprovals`),
    // não um filtro em cima da página: filtrar depois de ler mentiria na nota
    // "{n} de {m}" e esconderia linhas na segunda página.
    ...(identityId ? { identity_id: identityId } : {}),
  });

  // **A leitura do cluster que dá o selo é pedida daqui.**
  //
  // O backend deriva `em_execucao` da foto do cluster (aim-6ph.13) e **não** a
  // renova sozinho: FR-064 promete que rejeitar não chama o executor, e uma decisão
  // invalida esta fila. Então a tela que quer o selo pede a leitura nova, uma vez,
  // ao abrir (`POST /runtime/readings`); e quando a foto nova chega, refaz a fila
  // para as linhas virem com o campo. Até 10/10/2026 a tela fazia isso com a
  // leitura VIVA do agregado, que é a tela lendo o executor (regra 11).
  //
  // O erro é ignorado de propósito: executor fora do ar → foto velha → fila sem
  // selo, ordenada pela data do build. Nenhum banner: a fila não depende do
  // cluster para ser útil, e T15 é a tela que reporta o executor.
  const cluster = useClusterReading({ scope: 'cluster', refresh: [] });
  const since = cluster.started?.in_flight ? (cluster.started.started_at ?? null) : null;
  const photo = useClusterPhotoSince(since);
  const alreadyRefez = useRef(false);
  useEffect(() => {
    // O `POST` voltou e a foto já é a da leitura dele (ou a espera chegou ao teto). Sem resposta, o pedido falhou:
    // não há foto nova a esperar, e a fila fica como veio.
    if (!cluster.done || !photo.settled || alreadyRefez.current) return;
    alreadyRefez.current = true;
    if (cluster.started) void queue.refetch();
  }, [cluster.done, cluster.started, photo.settled, queue]);

  const itens = useMemo(() => queue.data?.items ?? [], [queue.data]);
  const grupos = useMemo(() => groupByIdentity(itens), [itens]);

  const linhas = useMemo<Row[]>(() => {
    const output: Row[] = [];
    for (const grupo of grupos) {
      output.push({
        kind: 'pendencia',
        id: grupo.principal.id,
        approval: grupo.principal,
        recolhida: false,
      });
      if (grupo.outras.length === 0) continue;

      const open = Boolean(abertos[grupo.identityId]);
      output.push({
        kind: 'group',
        id: `grupo-${grupo.identityId}`,
        identityId: grupo.identityId,
        quantas: grupo.outras.length,
        aberto: open,
      });
      if (!open) continue;
      for (const outra of grupo.outras) {
        output.push({ kind: 'pendencia', id: outra.id, approval: outra, recolhida: true });
      }
    }
    return output;
  }, [grupos, abertos]);

  const alternar = (identityId: string) =>
    setAbertos((current) => ({ ...current, [identityId]: !current[identityId] }));

  /** Célula de uma pendência; na linha de grupo a coluna fica vazia. */
  const celula = (linha: Row, content: (a: Approval) => ReactNode): ReactNode =>
    linha.kind === 'pendencia' ? content(linha.approval) : null;

  const columns: DataTableColumn<Row>[] = [
    {
      key: 'identity',
      header: t('approvals.pending.column.identity'),
      cell: (linha) => {
        if (linha.kind === 'group') {
          return (
            <button
              type="button"
              className={s.botaoGrupo}
              aria-expanded={linha.aberto}
              onClick={(e) => {
                e.stopPropagation();
                alternar(linha.identityId);
              }}
            >
              <Icon name={linha.aberto ? 'chevron-down' : 'chevron'} size="sm" aria-hidden="true" />
              {otherReleases(linha.quantas)}
            </button>
          );
        }
        const a = linha.approval;
        return (
          <span className={`${s.nome} ${linha.recolhida ? s.linhaRecolhida : ''}`}>
            <span className={s.nomeTexto}>
              <b>{identityName(a)}</b>
              <span>{a.id}</span>
            </span>
            {(a.other_pending ?? 0) > 0 ? (
              <InfoTip
                text={otherPendencyTip()}
                label={t('approvals.otherPendency.label')}
                size="xs"
              />
            ) : null}
          </span>
        );
      },
    },
    {
      key: 'approvalEvidence',
      header: t('approvals.evidence.column'),
      cell: (row) => celula(row, (approval) => <ApprovalEvidence approval={approval} />),
    },
    {
      key: 'reason',
      header: t('approvals.pending.column.reason'),
      cell: (linha) =>
        celula(linha, (a) => (
          <>
            <Badge tone={reasonBadge(a.reason).tone}>{reasonBadge(a.reason).rotulo}</Badge>
            {/*
              FR-078, nota de 29/08. Fica ao lado do motivo, e não na coluna de
              severity: «capabilities alteradas» não diz para que lado, e a
              severidade desta linha é `nenhuma` — é o selo que explica por que
              uma pendência sem severidade está esperando decisão.
            */}
            {a.reduction ? (
              <Badge tone="success" tip={reductionTip()}>
                {reductionLabel()}
              </Badge>
            ) : null}
            {a.auto_failed_code ? (
              <Badge tone="danger" tip={t('approvals.pending.autoFailed.tip')}>
                {t('approvals.pending.autoFailed', { code: a.auto_failed_code })}
              </Badge>
            ) : null}
          </>
        )),
    },
    {
      key: 'release',
      header: t('approvals.pending.column.release'),
      // **Tag e data do build**, não o digest (29/08). Com dezenas de tags por
      // imagem, `sha256:6a19c346…` é ruído para quem decide: o que separa duas
      // candidatas é a tag e quando cada uma foi construída. O digest inteiro está
      // em T10, e a base do diff continua na segunda linha (ajustes-front #11).
      cell: (linha) =>
        celula(linha, (a) => (
          <span className={s.releaseCelula} data-testid={`release-${a.id}`}>
            <span className={s.mono}>
              {a.release_tag ?? t('approvals.pending.noTag')}
              {a.release_built_at ? ` · ${dataCurta(a.release_built_at)}` : ''}
            </span>
            <span className={s.muted}>{comparadaWith(a.base_contract_version)}</span>
          </span>
        )),
    },
    {
      key: 'origin',
      header: t('approvals.pending.column.origin'),
      cell: (linha) => celula(linha, (a) => <Chip>{originLabel(a.origin)}</Chip>),
    },
    {
      key: 'desde',
      header: t('approvals.pending.column.waitingSince'),
      mono: true,
      cell: (linha) =>
        celula(linha, (a) => (
          <span className={s.muted}>{dataHour(a.created_at) ?? a.created_at}</span>
        )),
    },
    {
      key: 'severity',
      header: t('approvals.pending.column.severity'),
      cell: (linha) => celula(linha, (a) => <SeverityCelula approval={a} />),
    },
  ];

  return (
    <DataTable
      label={t('approvals.pending.table.label')}
      columns={columns}
      rows={linhas}
      rowKey={(linha) => linha.id}
      loading={queue.isLoading}
      error={queue.error}
      onRetry={() => void queue.refetch()}
      search={search}
      onSearchChange={setBusca}
      searchPlaceholder={t('approvals.pending.searchPlaceholder')}
      filters={
        <>
          {identityId ? (
            <ChipIdentity
              slug={itens[0]?.identity_slug ?? null}
              onRemover={onLimparIdentidade}
            />
          ) : null}
          <FilterCombo
            label={t('approvals.pending.filter.reason.label')}
            placeholder={t('approvals.pending.filter.reason.placeholder')}
            options={reasonOptions()}
            value={motivo}
            onChange={setMotivo}
          />
          <NoteCount
            exibidas={itens.length}
            total={total}
            sufixo={t('approvals.pending.note.suffix')}
          />
        </>
      }
      onClearFilters={() => {
        setBusca('');
        setMotivo(null);
        onLimparIdentidade();
      }}
      filtersApplied={Boolean(search || motivo || identityId)}
      emptyIcon="check-circle"
      emptyTitle={t(
        search || motivo || identityId
          ? 'approvals.pending.empty.filtered.title'
          : 'approvals.pending.empty.none.title',
      )}
      emptyMessage={t(
        search || motivo
          ? 'approvals.pending.empty.search.message'
          : identityId
            ? 'approvals.pending.empty.identity.message'
            : 'approvals.pending.empty.none.message',
      )}
      onRowClick={(linha) => {
        if (linha.kind === 'group') alternar(linha.identityId);
        else onAbrir(linha.approval.id);
      }}
    />
  );
}

/**
 * Severidade agregada da linha, com o selo «Em execução no cluster» à frente
 * quando é esta a release que os pods estão rodando (29/08).
 *
 * O selo só aparece com `em_execucao === true`. `false` e **`null`** não desenham
 * nada, e a diferença entre os dois não é cosmética: `null` é "ninguém leu o
 * cluster", e um selo derivado dele afirmaria execução que ninguém verificou — o
 * mesmo erro que AC-38 proíbe nos checks de T15.
 *
 * Identidade sem atribuição não é "risco alto": é bloqueio — a coluna diz
 * "atribuição pendente" e o drawer explica (FR-050).
 */
function SeverityCelula({ approval }: { approval: Approval }) {
  const selo =
    approval.running === true ? (
      <Badge tone="primary" tip={inExecutionTip()} className={s.seloExecucao}>
        {inExecution()}
      </Badge>
    ) : null;

  if (approval.incomplete_assignment) {
    return (
      <>
        {selo}
        <Badge tone="danger" tip={t('approvals.pending.assignmentPending.tip')}>
          {pendingAssignment()}
        </Badge>
      </>
    );
  }
  if (approval.diff_unavailable) {
    return (
      <>
        {selo}
        <Badge tone="neutral" tip={t('approvals.pending.diffUnavailable.tip')}>
          {t('approvals.pending.diffUnavailable')}
        </Badge>
      </>
    );
  }
  const severidade = approval.aggregated_severity ?? 'nenhuma';
  return (
    <>
      {selo}
      <Badge tone={severityBadge(severidade).tone}>{severityBadge(severidade).rotulo}</Badge>
    </>
  );
}
